import React, {
  Fragment,
  ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useApolloClient } from "@apollo/client";
import { groupBy, isEmpty, toUpper, upperCase } from "lodash";
import { useTranslation } from "react-i18next";
import { useHistory, useLocation } from "react-router-dom";
import { FEATURE_FLAG_NAME } from "common/constants";
import {
  OBJECT_TYPE,
  SUBMISSION_CATEGORY,
  SUBMISSION_TYPE_MODULE,
  SUPPORTED_LANGUAGES,
} from "common/constants";
import { buildLink } from "common/routing";
import { arrayHasAtLeastTwoItems } from "common/utils/arrays";
import {
  isSubmissionTable,
  SUBMISSION_TABLE_TO_CATEGORY,
  TABLE_NAMES,
  TopLevelTableNames,
} from "common/utils/queryBuilder";
import { getSubmissionCategoryTranslationKey } from "common/utils/submissions";
import { isNotNull } from "common/utils/tools";
import { ExtendedApolloClient } from "../../apollo";
import { SavedView } from "../../generated/graphql";
import { useLocalStorage } from "../../hooks/useLocalStorage";
import { spacing } from "../../stitches.config";
import {
  SELECTED_LANGUAGE_KEY,
  updateSelectedLanguage,
} from "../../utils/i18n/utils";
import { useOutsideAlerter } from "../../utils/outsideAlerter";
import { track } from "../../utils/tracking";
import { getCurrentURL } from "../../utils/url";
import { PublicUserContext } from "../Authentication/PublicUserContext";
import usePublicUserLoginModal from "../Authentication/PublicUserLoginModal";
import { AuthContext } from "../Authorization/AuthContext";
import { FlexColumn } from "../Common/__styles__/Layout";
import { AccountLogo } from "../Common/AccountLogo";
import AITranslationWarningBanner from "../Common/AITranslationWarningBanner";
import { Button } from "../Common/Button";
import { createSubmissionRedirect } from "../Common/CreateSubmissionButton/utils";
import { Icon } from "../Common/Icons/LucideIcons";
import { Body } from "../Common/Typography";
import { DropdownMenu } from "../Inputs";
import { LabelOption } from "../Inputs/__styles__/DropdownMenu";
import { ActionsProps, DropdownOption } from "../Inputs/DropdownMenu";
import { isSelfGeolocatingSubmissionType } from "../Submissions/utils";
import {
  FooterContainer,
  GUEST_WIDTH,
  HeaderContainer,
  HeaderContent,
  HomeButton,
  MenuParent,
  MenuRow,
  MenuRowSection,
  MenuSection,
  Page,
} from "./__styles__/Layout";
import { HeaderProfile } from "./HeaderProfile";
import { PublicBanner } from "./PublicBanner";
import { accountTitle, useIsMobileWidth, useIsTabletWidth } from "./utils";

const LANGUAGE_MAPPER = {
  en: "English",
  es: "Español",
};

const LanguageButton = ({ inMenu = false }: { inMenu?: boolean }) => {
  const { i18n } = useTranslation();
  const { setValue } = useLocalStorage({ key: SELECTED_LANGUAGE_KEY });
  const client = useApolloClient() as ExtendedApolloClient;
  const account = useContext(AuthContext).account!;
  const [languageMenuOpen, setLanguageMenuOpen] = useState(false);

  const supportedLanguages = account.publicPortal.supportedLanguages!;
  if (supportedLanguages!.length === 1) return null;

  const languageActions = supportedLanguages
    .map(language => {
      if (language === i18n.language) return null;
      return {
        label: LANGUAGE_MAPPER[language],
        onClick: () => {
          void updateSelectedLanguage({
            language,
            updateStaticTranslations: i18n.changeLanguage,
            updateDynamicTranslations: client.updateLanguage,
            updateLocalStorage: setValue,
          });

          track("Language changed on public website header", {
            browserLanguage: language,
          });
        },
      };
    })
    .filter(isNotNull);

  const selectedLanguageCopy =
    LANGUAGE_MAPPER[i18n.language as SUPPORTED_LANGUAGES];

  return inMenu ? (
    <FlexColumn css={{ gap: spacing.m }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
        onClick={() => setLanguageMenuOpen(!languageMenuOpen)}
      >
        <Button
          onClick={() => setLanguageMenuOpen(!languageMenuOpen)}
          size="medium"
          leftIconName="globe"
          styleVariant="buttonLinkDark"
        >
          {selectedLanguageCopy}
        </Button>
        <Icon
          iconName={languageMenuOpen ? "chevron-up" : "chevron-down"}
          size={16}
          color="contentPrimaryDark"
        />
      </div>
      {languageMenuOpen &&
        languageActions.map((action, index) => (
          <Button
            key={index}
            onClick={action.onClick!}
            styleVariant="buttonLinkDark"
            size="medium"
            style={{ paddingLeft: "24px" }}
          >
            {action.label}
          </Button>
        ))}
    </FlexColumn>
  ) : (
    <DropdownMenu
      actions={languageActions}
      customButton={({ onClick }) => (
        <Button
          onClick={onClick}
          size="small"
          leftIconName="globe"
          rightIconName="chevron-down"
          styleVariant="buttonLinkDark"
        >
          {selectedLanguageCopy}
        </Button>
      )}
      position="below-right"
    />
  );
};

const AccessRecordsButton = ({ inMenu = false }: { inMenu?: boolean }) => {
  const { t } = useTranslation();
  const { account, supportsElevationCertificates } = useContext(AuthContext);
  const [isInMenuDropdownOpen, setIsInMenuDropdownOpen] = useState(false);

  const viewsByTableName: Partial<
    Record<
      TopLevelTableNames,
      Array<Pick<SavedView, "id" | "name"> | { name: string }>
    >
  > = groupBy(account?.savedViews, savedView => savedView.query.source.table);

  if (supportsElevationCertificates) {
    viewsByTableName[TABLE_NAMES.PERMITS] ??= [];

    viewsByTableName[TABLE_NAMES.PERMITS].unshift({
      name: t("button-elevation-certificates"),
    });
  }

  // Ensure consistent ordering
  const sortedViewsByTableName = Object.entries(viewsByTableName).sort((a, b) =>
    a[0].localeCompare(b[0])
  );

  const actionsByLabel: Array<{ label: string; actions: Array<ActionsProps> }> =
    [];

  for (const [tableName, views] of sortedViewsByTableName) {
    let label = tableName;

    if (isSubmissionTable(tableName)) {
      const category = SUBMISSION_TABLE_TO_CATEGORY[tableName];
      if (category) {
        label = t(
          getSubmissionCategoryTranslationKey({ plural: true })[category]
        );
      }
    }

    actionsByLabel.push({
      label: upperCase(label),
      actions: views.map(view => {
        if ("id" in view) {
          return {
            label: view.name,
            href: buildLink("residentSavedView", { savedViewId: view.id }),
            lineWrap: true,
            variant: inMenu ? "contentDark" : undefined,
          };
        } else {
          return {
            label: view.name,
            href: buildLink("guestElevationCertificates", {
              accountId: account?.id,
            }),
            lineWrap: true,
            variant: inMenu ? "contentDark" : undefined,
          };
        }
      }),
    });
  }

  if (isEmpty(actionsByLabel)) return null;

  if (inMenu) {
    return (
      <>
        <Button
          styleVariant="buttonLinkDark"
          size="medium"
          onClick={() => setIsInMenuDropdownOpen(!isInMenuDropdownOpen)}
          rightIconName={isInMenuDropdownOpen ? "chevron-up" : "chevron-down"}
        >
          {t("access-records")}
        </Button>
        {isInMenuDropdownOpen && (
          <div
            style={{
              marginLeft: "-16px",
              // If there are many views, we need to enable scrolling.
              // The px offset is for header + some extra space on the bottom
              maxHeight: "calc(100vh - 300px)",
              overflowY: "auto",
            }}
          >
            {actionsByLabel.map(({ label, actions }, i) => {
              return (
                <Fragment key={i}>
                  <LabelOption>
                    <Body
                      size="small"
                      type="emphasis"
                      color="contentPrimaryDark"
                    >
                      {label}
                    </Body>
                  </LabelOption>
                  {actions
                    .filter(action => !action.hidden)
                    .map((action, index) => {
                      return (
                        <DropdownOption action={action} key={index} isGrouped />
                      );
                    })}
                </Fragment>
              );
            })}
          </div>
        )}
      </>
    );
  }

  return (
    <DropdownMenu
      actions={actionsByLabel}
      actionListWidth="360px"
      customButton={({ onClick }) => (
        <Button
          styleVariant="buttonLinkDark"
          size="small"
          onClick={onClick}
          rightIconName="chevron-down"
        >
          {t("access-records")}
        </Button>
      )}
      position="below-left"
    />
  );
};

const SubmitButton = ({ inMenu = false }: { inMenu?: boolean }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const [isInMenuDropdownOpen, setIsInMenuDropdownOpen] = useState(false);
  const { account } = useContext(AuthContext);
  const supportElevationCertificateUploads =
    account?.publicPortal.supportsDocumentUploads;

  const actionsByLabel: Array<{ label: string; actions: Array<ActionsProps> }> =
    [];

  if (supportElevationCertificateUploads) {
    const ecSubmissionType = account?.submissionTypes.find(
      submissionType =>
        submissionType.canCreate.includes("guest") &&
        submissionType.modules.find(
          module => module === SUBMISSION_TYPE_MODULE.EC_SUBMISSION
        )
    );

    if (ecSubmissionType) {
      actionsByLabel.push({
        label: toUpper(
          t(
            getSubmissionCategoryTranslationKey({ plural: true })[
              ecSubmissionType!.category as SUBMISSION_CATEGORY
            ]
          )
        ),
        actions: [
          {
            label: ecSubmissionType!.name,
            onClick: () =>
              history.push(
                buildLink("guestUploadElevationCertificates", {
                  accountId: account.id,
                }),
                { prevLocation: getCurrentURL(location) }
              ),
            lineWrap: true,
            variant: inMenu ? "contentDark" : undefined,
          },
        ],
      });
    }
  }

  const externalSubmissionTypes = account?.submissionTypes.filter(
    submissionType =>
      submissionType.canCreate.includes("guest") &&
      submissionType.attachments.find(
        submissionType => submissionType.type === OBJECT_TYPE.PROPERTY
      ) &&
      !isSelfGeolocatingSubmissionType({
        submissionType,
        accountDocumentTypes: account?.accountDocumentTypes,
      })
  );

  if (externalSubmissionTypes) {
    const groupedExternalSubmissionTypes = groupBy(
      externalSubmissionTypes,
      "category"
    );
    Object.entries(groupedExternalSubmissionTypes).forEach(
      ([groupLabel, submissionTypes]) => {
        const label = toUpper(
          t(
            getSubmissionCategoryTranslationKey({ plural: true })[
              groupLabel as SUBMISSION_CATEGORY
            ]
          )
        );
        if (!actionsByLabel.find(action => action.label === label)) {
          actionsByLabel.push({
            label,
            actions: [],
          });
        }
        actionsByLabel
          .find(action => action.label === label)!
          .actions.push(
            ...submissionTypes.map(submissionType => ({
              label: submissionType.name,
              onClick: () =>
                createSubmissionRedirect({
                  history,
                  submissionTypeId: submissionType.id,
                  isGlobal: true,
                }),
              lineWrap: true,
              variant: inMenu ? ("contentDark" as const) : undefined,
            }))
          );
      }
    );
  }

  const workflowDefinitions = account?.workflowDefinitions;

  if (workflowDefinitions?.length) {
    actionsByLabel.push({
      label: toUpper(t("common-workflows")),
      actions: workflowDefinitions.map(workflowDefinition => ({
        label: workflowDefinition.name,
        onClick: () => {
          history.push(
            buildLink("workflowDefinition", {
              workflowDefinitionId: workflowDefinition.id,
            })
          );
        },
        lineWrap: true,
        variant: inMenu ? ("contentDark" as const) : undefined,
      })),
    });
  }

  if (isEmpty(actionsByLabel)) return null;

  if (inMenu) {
    return (
      <>
        <Button
          styleVariant="buttonLinkDark"
          size="medium"
          onClick={() => setIsInMenuDropdownOpen(!isInMenuDropdownOpen)}
          rightIconName={isInMenuDropdownOpen ? "chevron-up" : "chevron-down"}
        >
          {t("submit")}
        </Button>
        {isInMenuDropdownOpen && (
          <div
            style={{
              marginLeft: "-16px",
              // If there are many views, we need to enable scrolling.
              // The px offset is for header + some extra space on the bottom
              maxHeight: "calc(100vh - 450px)",
              overflowY: "auto",
            }}
          >
            {actionsByLabel.map(({ label, actions }, i) => {
              return (
                <Fragment key={i}>
                  <LabelOption>
                    <Body
                      size="small"
                      type="emphasis"
                      color="contentPrimaryDark"
                    >
                      {label}
                    </Body>
                  </LabelOption>
                  {actions
                    .filter(action => !action.hidden)
                    .map((action, index) => {
                      return (
                        <DropdownOption action={action} key={index} isGrouped />
                      );
                    })}
                </Fragment>
              );
            })}
          </div>
        )}
      </>
    );
  }

  return (
    <DropdownMenu
      actions={actionsByLabel}
      actionListWidth="360px"
      customButton={({ onClick }) => (
        <Button
          styleVariant="buttonLinkDark"
          size="small"
          onClick={onClick}
          rightIconName="chevron-down"
        >
          {t("submit")}
        </Button>
      )}
      position="below-left"
    />
  );
};

const GetHelpButton = ({
  inMenu = false,
  propertyId,
}: {
  inMenu?: boolean;
  propertyId?: string;
}) => {
  const history = useHistory();
  const { t } = useTranslation();
  const account = useContext(AuthContext).account!;

  const onGetHelpClick = () =>
    history.push(
      buildLink("getHelp", { accountId: account.id }, { propertyId })
    );

  const style = inMenu
    ? undefined
    : { minWidth: "unset", whiteSpace: "nowrap" as const };

  return (
    <Button
      onClick={onGetHelpClick}
      styleVariant="buttonLinkDark"
      size={inMenu ? "medium" : "small"}
      style={style}
    >
      {t("contact-us")}
    </Button>
  );
};

const HeaderRowMenu = ({
  propertyId,
  onClickSignIn,
}: {
  propertyId?: string;
  onClickSignIn: () => void;
}) => {
  const { isFeatureEnabled, account } = useContext(AuthContext);

  const showLanguageButton = arrayHasAtLeastTwoItems(
    account!.publicPortal.supportedLanguages
  );
  const showHeaderProfile = isFeatureEnabled(FEATURE_FLAG_NAME.PUBLIC_USERS);

  return (
    <MenuRow>
      <MenuRowSection>
        <AccessRecordsButton />
        <SubmitButton />
        <GetHelpButton propertyId={propertyId} />
      </MenuRowSection>
      {showLanguageButton || showHeaderProfile ? (
        <MenuRowSection>
          <LanguageButton />
          <HeaderProfile onClickSignIn={onClickSignIn} />
        </MenuRowSection>
      ) : null}
    </MenuRow>
  );
};

const ExpandedMenu = ({
  propertyId,
  onClickSignIn,
}: {
  propertyId?: string;
  onClickSignIn: () => void;
}) => {
  const { account, isFeatureEnabled } = useContext(AuthContext);
  const showLanguageButton = arrayHasAtLeastTwoItems(
    account!.publicPortal.supportedLanguages
  );
  const showHeaderProfile = isFeatureEnabled(FEATURE_FLAG_NAME.PUBLIC_USERS);
  return (
    <MenuParent style={{ background: account!.publicPortal.color }}>
      <MenuSection>
        <AccessRecordsButton inMenu />
        <SubmitButton inMenu />
        <GetHelpButton inMenu propertyId={propertyId} />
        {showLanguageButton || showHeaderProfile ? (
          <MenuSection>
            <LanguageButton inMenu />
            <HeaderProfile inMenu onClickSignIn={onClickSignIn} />
          </MenuSection>
        ) : null}
      </MenuSection>
    </MenuParent>
  );
};

const MenuButton = ({ onClick }: { onClick: () => void }) => (
  <Icon
    iconName="align-justify"
    onClick={onClick}
    size={16}
    color="contentPrimaryDark"
    testId="menu-button"
  />
);

interface HeaderProps {
  propertyId?: string;
}

export const Header = ({ propertyId }: HeaderProps) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const { refetchCurrentPublicUser } = useContext(PublicUserContext);
  const wrapperRef = useRef(null);
  const account = useContext(AuthContext).account!;
  const isMobileScreen = useIsMobileWidth();
  const isTabletScreen = useIsTabletWidth();

  const isSmallScreen = isMobileScreen || isTabletScreen;

  useEffect(() => {
    setMenuOpen(false);
  }, [isSmallScreen]);

  const [showPublicUserLoginModal] = usePublicUserLoginModal({
    onLoginSuccess: () => {
      void refetchCurrentPublicUser();
    },
  });

  const onClickSignIn = () => {
    setMenuOpen(false);
    showPublicUserLoginModal();
  };

  // when someone clicks outside of the mobile menu the menu will disappear
  useOutsideAlerter({
    ref: wrapperRef,
    onOutsideInteraction: () => setMenuOpen(false),
  });

  const subtitle = account.publicPortal.navBarTitle;

  return (
    <>
      <HeaderContainer
        style={{ background: account.publicPortal.color }}
        ref={wrapperRef}
        id="header"
      >
        <HeaderContent>
          <HomeButton to={buildLink("guestHome", { accountId: account.id })}>
            {account.logoUrl && (
              <AccountLogo account={account} style={{ marginRight: "8px" }} />
            )}
            <div>
              <Body
                testId="resident-main-header"
                size="large"
                type="emphasis"
                color="contentPrimaryDark"
              >
                {accountTitle()}
              </Body>
              <Body
                testId="resident-subheader"
                size="small"
                type="regular"
                color="contentPrimaryDark"
              >
                {subtitle}
              </Body>
            </div>
          </HomeButton>
          {isSmallScreen ? (
            <MenuButton onClick={() => setMenuOpen(!menuOpen)} />
          ) : (
            <HeaderRowMenu
              propertyId={propertyId}
              onClickSignIn={onClickSignIn}
            />
          )}
        </HeaderContent>
        {menuOpen && (
          <ExpandedMenu propertyId={propertyId} onClickSignIn={onClickSignIn} />
        )}
      </HeaderContainer>
      <AITranslationWarningBanner />
    </>
  );
};

export const Footer = () => {
  const { t } = useTranslation();
  return (
    <FooterContainer id="footer">
      <Body size="default" type="regular" color="contentPlaceholder">
        <a href="https://www.withforerunner.com/" target="_blank">
          {t("footer-forerunner")}
        </a>{" "}
        |{" "}
        <a
          href="https://www.withforerunner.com/legal/terms-of-services"
          target="_blank"
        >
          {t("footer-terms")}
        </a>
      </Body>
    </FooterContainer>
  );
};

type LayoutProps = {
  propertyId?: string;
  hasGutters?: boolean;
  children: ReactNode;
};

const Layout = ({ propertyId, children, hasGutters = false }: LayoutProps) => {
  const childrenDisplay = hasGutters ? (
    <div
      style={{ maxWidth: GUEST_WIDTH, marginLeft: "auto", marginRight: "auto" }}
    >
      {children}
    </div>
  ) : (
    children
  );
  return (
    <Page>
      <PublicBanner />
      <Header propertyId={propertyId} />
      {childrenDisplay}
      <Footer />
    </Page>
  );
};

export default Layout;
